import type { ComponentType, MouseEventHandler } from "react";
import React, { useCallback } from "react";

import { useLogger } from "../logger";
import { useDataMeshTracking } from "../tracking";
import {
  WNG_HTML_EVENT_ID_PROP_DEPRECATED,
  WNG_NO_WITH_MODULE_CONTEXT,
  WNG_TRACKING_NOT_SENT_HTML_EVENT_ID,
} from "./constants";
import { useDatameshNamespace } from "./DatameshNamespaceProvider";

function getDisplayName<TProps>(WrappedComponent: ComponentType<TProps>) {
  return WrappedComponent.displayName || WrappedComponent.name || "Component";
}

const buildTracking = (
  namespace: string,
  id: string | undefined,
  htmlEventIdProp: string | undefined,
  logger: {
    warn: (log: string) => void;
  },
) => {
  if (!namespace || namespace === "") {
    logger.warn(WNG_NO_WITH_MODULE_CONTEXT);
    return;
  }

  if (!id && !htmlEventIdProp) {
    logger.warn(WNG_TRACKING_NOT_SENT_HTML_EVENT_ID);
    return;
  }

  if (!id) {
    id = htmlEventIdProp;
  } else {
    logger.warn(WNG_HTML_EVENT_ID_PROP_DEPRECATED);
  }

  return { id: `${namespace}-${id}` };
};

type OnClickHTMLElement<T, U> = T extends {
  onClick: MouseEventHandler<infer TElement>;
}
  ? TElement
  : U;

export const withClickTrack = <
  TProps,
  TElement = OnClickHTMLElement<TProps, HTMLDivElement>,
>(
  Component: ComponentType<TProps & { onClick?: MouseEventHandler<TElement> }>,
  id: string,
) => {
  const TrackedComponent = (
    props: {
      onClick: MouseEventHandler<TElement>;
    } & TProps & { "html-event-id"?: string } & JSX.IntrinsicAttributes,
  ) => {
    const logger = useLogger();
    const namespace = useDatameshNamespace();
    const { trackClick } = useDataMeshTracking();
    const htmlEventIdProp = props["html-event-id"];
    const onClickDecorated: MouseEventHandler<TElement> = useCallback(
      (...args) => {
        const trackingObj = buildTracking(
          namespace,
          id,
          htmlEventIdProp,
          logger,
        );
        if (trackingObj) {
          trackClick(trackingObj);
        }
        return props.onClick?.(...args);
      },
      [htmlEventIdProp, logger, namespace, props, trackClick],
    );
    return <Component {...props} onClick={onClickDecorated} />;
  };
  TrackedComponent.displayName = `withClickTrack(${getDisplayName(Component)})`;
  return TrackedComponent;
};
